import React, { useState, useEffect, useRef, useMemo } from 'react';
import './loader.scss';

// Import background images from assets
import chaouen from '../../assets/chaouenHeader.jpg';
import sunset from '../../assets/blogHeader.jpg';
import halong from '../../assets/halong.jpg';
import marietas from '../../assets/marietas.jpg';
import waterfall from '../../assets/waterfall.jpg';

const backgroundImages = [chaouen, halong, marietas, waterfall, sunset];

const Loader = ({ loaded = 0, total = 0, language = 'en' }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [smoothProgress, setSmoothProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const progressRef = useRef(0);
  const rafRef = useRef(null);

  // Lock scroll when loader is active
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, []);

  const messages = useMemo(() => ({
    en: [
      "Preparing your visual journey",
      "Loading captured moments",
      "Curating the gallery",
      "Almost ready to explore",
    ],
    es: [
      "Preparando tu viaje visual",
      "Cargando momentos capturados",
      "Curando la galería",
      "Casi listo para explorar",
    ],
    fr: [
      "Préparation de votre voyage visuel",
      "Chargement des moments capturés",
      "Conservation de la galerie",
      "Presque prêt à explorer",
    ],
    zh: [
      "准备您的视觉之旅",
      "加载捕捉的时刻",
      "策划画廊",
      "即将准备好探索",
    ]
  }), []);

  // Start at 10% immediately for better perceived performance
  // Scale 0-100% to 10-100% so users never see 0%
  const actualProgress = total > 0
    ? Math.min(Math.round(10 + (loaded / total) * 90), 100)
    : 10;

  // Smooth progress animation
  useEffect(() => {
    const animateProgress = () => {
      const diff = actualProgress - progressRef.current;
      const step = Math.max(diff * 0.08, 0.3);

      if (Math.abs(diff) > 0.1) {
        progressRef.current += step;
        setSmoothProgress(Math.min(Math.round(progressRef.current), 100));
        rafRef.current = requestAnimationFrame(animateProgress);
      } else {
        progressRef.current = actualProgress;
        setSmoothProgress(actualProgress);
      }
    };

    rafRef.current = requestAnimationFrame(animateProgress);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [actualProgress]);

  // Rotate background images and messages
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % backgroundImages.length);
      setCurrentMessageIndex(prev => (prev + 1) % messages[language || 'en'].length);
    }, 4000);

    return () => clearInterval(interval);
  }, [language, messages]);

  // Preload first image
  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = backgroundImages[0];
  }, []);

  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (smoothProgress / 100) * circumference;

  return (
    <div className="modern-loader">
      {/* Animated background slideshow */}
      <div className="loader-backgrounds">
        {backgroundImages.map((img, index) => (
          <div
            key={index}
            className={`loader-bg-image ${index === currentImageIndex ? 'active' : ''}`}
            style={{ backgroundImage: `url(${img})` }}
          />
        ))}
        <div className="loader-overlay" />
      </div>

      <div className="loader-content">
        {/* Massive Percentage Display */}
        <div className="giant-progress">
          <span className="giant-value">{smoothProgress}</span>
          <span className="giant-symbol">%</span>
        </div>

        {/* Loading Bar at Bottom */}
        <div className="loading-bar-container">
          <div className="loading-bar" style={{ width: `${smoothProgress}%` }}></div>
        </div>

        {/* Loading message */}
        <div className="loading-message">
          <p className="message-text" key={currentMessageIndex}>
            {messages[language || 'en'][currentMessageIndex]}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Loader;
